import {
  Alert,
  Avatar,
  Box,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Percent as PercentIcon,
} from '@mui/icons-material';
import { StepNavigation } from '../../molecules/StepNavigation';
import { GangFinancialSummary } from '../../molecules/GangFinancialSummary';
import { GangRoleBadge } from '../../atoms/GangRoleBadge';
import { GangLifecycleMode } from '../../../stores/gangLifecycleStore';
import { UseFormReturn } from 'react-hook-form';
import {
  GangCreationFormData,
  GangParticipant,
} from '../../../types/gangCreationTypes';

const getInitials = (name: string) => {
  if (!name) {
    return 'U';
  }

  return name
    .split(' ')
    .filter(Boolean)
    .map(part => part.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);

const renderSplitChip = (
  percentage: number | null | undefined,
  isEqualSplit: boolean,
  equalPercentage: number
) => {
  // When equal split is enabled, show the calculated equal percentage
  if (isEqualSplit) {
    return (
      <Chip
        key="equal-split"
        icon={<PercentIcon fontSize="small" />}
        label={`${equalPercentage}% (Equal Split)`}
        size="small"
        color="info"
        variant="outlined"
        sx={{ fontWeight: 600 }}
      />
    );
  }

  if (percentage === null || percentage === undefined) {
    return (
      <Chip
        key="no-percentage"
        label="No split percentage"
        size="small"
        color="default"
        variant="outlined"
        sx={{ fontStyle: 'italic' }}
      />
    );
  }

  // Round to 1 d.p. to avoid floating-point display issues (e.g., 33.300000000000004%)
  const roundedPercentage = Math.round(percentage * 10) / 10;

  return (
    <Chip
      key="percentage"
      icon={<PercentIcon fontSize="small" />}
      label={`${roundedPercentage}%`}
      size="small"
      color="info"
      variant="outlined"
      sx={{ fontWeight: 600 }}
    />
  );
};

const renderFixedRateChip = (
  defaultRate: number | null | undefined,
  defaultUnitsValue: number | null | undefined
) => {
  if (defaultRate === null || defaultRate === undefined) {
    return (
      <Chip
        key="no-fixed-rate"
        label="No fixed rate"
        size="small"
        color="default"
        variant="outlined"
        sx={{ fontStyle: 'italic' }}
      />
    );
  }

  const rateLabel =
    defaultUnitsValue !== null && defaultUnitsValue !== undefined
      ? `${formatCurrency(defaultRate)} × ${defaultUnitsValue} units`
      : formatCurrency(defaultRate);

  return (
    <Chip
      key="fixedRate"
      label={rateLabel}
      size="small"
      color="secondary"
      variant="outlined"
      sx={{ fontWeight: 600 }}
    />
  );
};

const renderVisibilityChip = (canSeeSplit: boolean) => (
  <Chip
    icon={
      canSeeSplit ? (
        <VisibilityIcon fontSize="small" />
      ) : (
        <VisibilityOffIcon fontSize="small" />
      )
    }
    label={canSeeSplit ? 'Can see splits' : 'Cannot see splits'}
    size="small"
    color={canSeeSplit ? 'success' : 'warning'}
    variant={canSeeSplit ? 'filled' : 'outlined'}
    sx={{ fontWeight: 600 }}
  />
);

export interface GangReviewStepProps {
  form: UseFormReturn<GangCreationFormData>;
  mode: GangLifecycleMode;
  onBack: () => void;
  onSubmit: () => void;
  isSubmitting: boolean;
  hasChanges: boolean;
  isValid?: boolean;
}

export const GangReviewStep = ({
  form,
  mode,
  onBack,
  onSubmit,
  isSubmitting,
  hasChanges,
  isValid = true,
}: GangReviewStepProps) => {
  const gangName = form.watch('name');
  const leaderIds = form.watch('leaderIds') ?? [];
  const memberIds = form.watch('memberIds') ?? [];
  const participants = form.watch('participants') ?? {};
  const isEqualSplit = form.watch('isEqualSplit') ?? true;

  const leaderParticipants: GangParticipant[] = leaderIds
    .map(userId => participants[userId])
    .filter((participant): participant is GangParticipant =>
      Boolean(participant)
    );
  const memberParticipants: GangParticipant[] = memberIds
    .map(userId => participants[userId])
    .filter((participant): participant is GangParticipant =>
      Boolean(participant)
    );

  // Count percentage members and calculate equal split
  const percentageMemberCount = [
    ...leaderParticipants,
    ...memberParticipants,
  ].filter(p => p.paymentType === 'percentage').length;

  const equalPercentage =
    percentageMemberCount > 0
      ? Math.round((100 / percentageMemberCount) * 10) / 10
      : 0;

  const totalMembers = leaderParticipants.length + memberParticipants.length;
  const totalRegularMembers = memberParticipants.length;

  // When equal split is enabled, total is always 100%
  // Otherwise, sum up the stored percentages
  const totalPercentageSplit = isEqualSplit
    ? percentageMemberCount > 0
      ? 100
      : 0
    : [...leaderParticipants, ...memberParticipants]
        .filter(
          member =>
            member.defaultSplitPercentage !== null &&
            member.defaultSplitPercentage !== undefined
        )
        .reduce((sum, member) => sum + (member.defaultSplitPercentage || 0), 0);

  const hasAtLeastTwoMembers = totalMembers >= 2;
  const hasAtLeastOneLeader = leaderParticipants.length >= 1;
  const meetsMinimumRequirements = hasAtLeastTwoMembers && hasAtLeastOneLeader;

  const disableSubmit =
    isSubmitting ||
    !isValid ||
    !meetsMinimumRequirements ||
    (mode === 'edit' && !hasChanges && totalMembers > 0);

  return (
    <Box sx={{ py: 2 }}>
      <Typography variant="body1" sx={{ mb: 2 }}>
        Review your configuration before{' '}
        {mode === 'create' ? 'creating the gang.' : 'saving changes.'}
      </Typography>

      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" fontWeight="medium">
          Gang Name
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          data-testid="gang-review-name-value"
        >
          {gangName}
        </Typography>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" fontWeight="medium">
          Team Summary
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          data-testid="gang-review-team-summary"
        >
          {leaderParticipants.length} leader
          {leaderParticipants.length === 1 ? '' : 's'} &bull;{' '}
          {totalRegularMembers} member{totalRegularMembers === 1 ? '' : 's'}{' '}
          &bull; {totalMembers} total participant{totalMembers === 1 ? '' : 's'}
        </Typography>
      </Box>

      <GangFinancialSummary
        leaders={leaderParticipants}
        members={memberParticipants}
        isEqualSplit={isEqualSplit}
      />

      {!isEqualSplit && totalPercentageSplit > 100 && (
        <Alert
          severity="error"
          sx={{ mt: 2 }}
          data-testid="gang-review-overallocation-alert"
        >
          Default splits exceed 100%. Adjust the percentages before continuing.
        </Alert>
      )}

      {!hasAtLeastTwoMembers && (
        <Alert
          severity="error"
          sx={{ mt: 2 }}
          data-testid="gang-review-minimum-members-alert"
        >
          A gang must have at least 2 members. Please add more members.
        </Alert>
      )}

      {!hasAtLeastOneLeader && (
        <Alert
          severity="error"
          sx={{ mt: 2 }}
          data-testid="gang-review-minimum-leaders-alert"
        >
          A gang must have at least 1 leader. Please add a leader.
        </Alert>
      )}

      {mode === 'edit' && !hasChanges && (
        <Alert
          severity="info"
          sx={{ mt: 2 }}
          data-testid="gang-review-no-changes-alert"
        >
          No changes detected yet. Update the gang details to enable saving.
        </Alert>
      )}

      <Box sx={{ mt: 4 }}>
        <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 2 }}>
          Leaders
        </Typography>

        <List dense data-testid="gang-review-leaders-list">
          {leaderParticipants.map(leader => (
            <ListItem
              key={leader.userId}
              sx={{ px: 0 }}
              data-testid={`gang-review-leader-${leader.userId}`}
            >
              <ListItemAvatar>
                <Avatar color="primary">{getInitials(leader.fullName)}</Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Typography variant="body1" fontWeight="medium">
                      {leader.fullName}
                    </Typography>
                    <GangRoleBadge role="leader" />
                  </Box>
                }
                secondary={
                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {leader.email || 'Email unavailable'}
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>{renderVisibilityChip(true)}</Box>
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ mt: 0.5, flexWrap: 'wrap' }}
                    >
                      {leader.paymentType === 'percentage'
                        ? renderSplitChip(
                            leader.defaultSplitPercentage,
                            isEqualSplit,
                            equalPercentage
                          )
                        : renderFixedRateChip(
                            leader.defaultRate,
                            leader.defaultUnitsValue
                          )}
                    </Stack>
                  </Box>
                }
              />
            </ListItem>
          ))}
        </List>
      </Box>

      <Box sx={{ mt: 4 }}>
        <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 2 }}>
          Members
        </Typography>

        {memberParticipants.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No additional members selected.
          </Typography>
        ) : (
          <List dense data-testid="gang-review-members-list">
            {memberParticipants.map(member => (
              <ListItem
                key={member.userId}
                sx={{ px: 0 }}
                data-testid={`gang-review-member-${member.userId}`}
              >
                <ListItemAvatar>
                  <Avatar>{getInitials(member.fullName)}</Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        flexWrap: 'wrap',
                      }}
                    >
                      <Typography variant="body1" fontWeight="medium">
                        {member.fullName}
                      </Typography>
                      <GangRoleBadge role="member" />
                    </Box>
                  }
                  secondary={
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.5,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {member.email || 'Email unavailable'}
                      </Typography>
                      <Box sx={{ mt: 0.5 }}>
                        {renderVisibilityChip(Boolean(member.canSeeSplit))}
                      </Box>
                      <Stack
                        direction="row"
                        spacing={1}
                        sx={{ mt: 0.5, flexWrap: 'wrap' }}
                      >
                        {member.paymentType === 'percentage'
                          ? renderSplitChip(
                              member.defaultSplitPercentage,
                              isEqualSplit,
                              equalPercentage
                            )
                          : renderFixedRateChip(
                              member.defaultRate,
                              member.defaultUnitsValue
                            )}
                      </Stack>
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
        )}
      </Box>

      <StepNavigation
        onPrevious={onBack}
        onNext={onSubmit}
        nextLabel={mode === 'create' ? 'Create Gang' : 'Save Changes'}
        disableNext={disableSubmit}
        endAdornment={isSubmitting ? <CircularProgress size={24} /> : undefined}
      />
    </Box>
  );
};
